'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, Search } from 'lucide-react';

const navLinks = [
  { label: 'Accueil', href: '/home-search', key: 'nav-home' },
  { label: 'Comparer', href: '/search-results', key: 'nav-compare' },
  { label: 'Meilleures offres', href: '/search-results?sort=best-deal', key: 'nav-deals' },
];

export default function Topbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchVal, setSearchVal] = useState('');
  const router = useRouter();

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (searchVal.trim()) {
      router.push(`/search-results?q=${encodeURIComponent(searchVal.trim())}`);
    }
  }

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-orange-100 shadow-sm">
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 h-16 flex items-center gap-4">
        {/* Logo */}
        <Link href="/home-search" className="flex items-center gap-2 shrink-0">
          <AppLogo size={36} />
          <span className="font-display font-800 text-xl text-orange-600 hidden sm:block tracking-tight">
            Croquette<span className="text-teal-600">Compare</span>
          </span>
        </Link>

        {/* Search bar — desktop */}
        <form
          onSubmit={handleSearch}
          className="hidden md:flex flex-1 max-w-xl mx-auto items-center bg-orange-50 border border-orange-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-orange-400 focus-within:border-orange-400 transition-all"
        >
          <Search size={16} className="ml-3 text-orange-400 shrink-0" />
          <input
            type="text"
            value={searchVal}
            onChange={(e) => setSearchVal(e.target.value)}
            placeholder="Rechercher une marque ou un produit…"
            className="flex-1 bg-transparent px-3 py-2 text-sm text-gray-800 placeholder-orange-300 outline-none"
          />
          <button
            type="submit"
            className="bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-2 transition-colors duration-150"
          >
            Chercher
          </button>
        </form>

        {/* Nav links — desktop */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.key}
              href={link.href}
              className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:text-orange-600 hover:bg-orange-50 transition-colors duration-150"
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="lg:hidden ml-auto p-2 rounded-lg hover:bg-orange-50 transition-colors"
          aria-label="Ouvrir le menu"
        >
          {mobileOpen ? <X size={20} className="text-gray-700" /> : <Menu size={20} className="text-gray-700" />}
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-orange-100 px-4 py-4 space-y-3 animate-fade-in">
          <form onSubmit={handleSearch} className="flex items-center bg-orange-50 border border-orange-200 rounded-xl overflow-hidden">
            <Search size={16} className="ml-3 text-orange-400 shrink-0" />
            <input
              type="text"
              value={searchVal}
              onChange={(e) => setSearchVal(e.target.value)}
              placeholder="Rechercher…"
              className="flex-1 bg-transparent px-3 py-2 text-sm outline-none"
            />
            <button type="submit" className="bg-orange-500 text-white text-sm font-semibold px-4 py-2">
              OK
            </button>
          </form>
          {navLinks.map((link) => (
            <Link
              key={link.key}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              className="block px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-orange-600 hover:bg-orange-50 transition-colors"
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}